import { scroller } from 'react-scroll';
import { type FormikProps, withFormik } from 'formik';
import * as Yup from 'yup';
import React, { type RefObject, useEffect, useRef } from 'react';
import styled from '@emotion/styled';

import { PrimaryButton } from '@/components/button';
import { Row, Col } from '@/components/grid';
import { TextField, Recaptcha } from '@/components/form-fields';
import { ControlledSelect } from '@/components/form-fields/select';
import formValidation from '@/utils/form-validation';
import { logTrackingEvent } from '@/utils/tracking';
import Alert from '@/components/alert';
import { ALERT_STATUSES } from '@/constants/statuses';

const StyledCol = styled(Col)`
  padding-bottom: 1rem;
  position: relative;
`;

const ErrorCol = styled(Col)`
  position: relative;
`;

const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

const colWidth = { xs: 12, lg: 10, xl: 8 };
const colOffset = [0, 0, 0, 1 / 12, 2 / 12];
const semiColWidth = { xs: 12, md: 6, lg: 5, xl: 4 };

const focusInputRef = (ref: RefObject<HTMLInputElement | null>) => {
  return setTimeout(() => ref.current?.focus(), 100);
};

const formEvent = {
  event: 'form_engagement',
  form_id: 0,
  form_name: 'contact-us',
};

type ContactFormFieldValues = {
  firstName?: string;
  lastName?: string;
  fromEmail?: string;
  state?: string;
  message?: string;
  recaptcha?: string | null;
};

type ContactFormErrors = ContactFormFieldValues;

type ContactFormOuterProps = {
  subject?: string;
  successMessage?: string;
};

type ContactFormProps = FormikProps<Record<string, string>> &
  ContactFormOuterProps & {
    errors: ContactFormErrors;
  };

const ContactForm = ({
  errors,
  handleChange,
  handleReset,
  handleSubmit,
  isSubmitting,
  setStatus,
  status,
  successMessage,
  touched,
  values,
}: ContactFormProps) => {
  const fNameRef = useRef<HTMLInputElement>(null);
  const lNameRef = useRef(null);
  const emailRef = useRef(null);
  const messageRef = useRef(null);

  const hasStartedRef = useRef(false);

  const scrollTo = (elementId: string) => {
    if (document.getElementById(elementId)) {
      scroller.scrollTo(elementId, {
        duration: 800,
        offset: -50,
        smooth: true,
      });
    }
  };

  useEffect(() => {
    const validationErrorFields = Object.keys(errors || {});

    if (!isSubmitting) {
      return;
    }

    if (status === 'success') {
      return scrollTo('form-success');
    }

    if (validationErrorFields.length > 0) {
      scrollTo(validationErrorFields[0]);
    }
  }, [status, errors, isSubmitting]);

  const resetForm = () => {
    setStatus(null);
    scrollTo('form-success');
    handleReset();
  };

  const isSuccess = status === 'success';

  useEffect(() => {
    if (fNameRef && errors?.firstName) {
      focusInputRef(fNameRef);
    } else if (lNameRef && errors?.lastName) {
      focusInputRef(lNameRef);
    } else if (emailRef && errors?.fromEmail) {
      focusInputRef(emailRef);
    } else if (messageRef && errors?.message) {
      focusInputRef(messageRef);
    }
  }, [fNameRef, lNameRef, emailRef, messageRef, isSubmitting]);

  const trackFormStart = () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    logTrackingEvent({
      ...formEvent,
      form_step: 'start',
    });
  };

  return (
    <>
      {isSuccess && (
        <Row wrap="wrap" id="form-success">
          <StyledCol width={colWidth} offset={colOffset}>
            <Alert status={ALERT_STATUSES.success} content={successMessage} />
          </StyledCol>
          <StyledCol width={semiColWidth} offset={colOffset}>
            <PrimaryButton eventText="Reset" type="button" disabled={isSubmitting} onClick={resetForm}>
              Reset
            </PrimaryButton>
          </StyledCol>
        </Row>
      )}
      {!isSuccess && (
        <form onSubmit={handleSubmit} onFocus={trackFormStart} autoComplete="on" aria-label="contact form">
          <Row wrap="wrap">
            <StyledCol width={semiColWidth} offset={colOffset}>
              <TextField
                error={touched.firstName && Boolean(errors?.firstName)}
                fullWidth
                helperText={touched.firstName ? errors?.firstName : ''}
                id="firstName"
                label="First Name"
                onChange={handleChange}
                value={values.firstName}
                disabled={isSubmitting}
                autoComplete="given-name"
                inputRef={fNameRef}
              />
            </StyledCol>
            <StyledCol width={semiColWidth}>
              <TextField
                error={touched.lastName && Boolean(errors?.lastName)}
                fullWidth
                helperText={touched.lastName ? errors?.lastName : ''}
                id="lastName"
                label="Last Name"
                onChange={handleChange}
                value={values.lastName}
                disabled={isSubmitting}
                autoComplete="family-name"
                inputRef={lNameRef}
              />
            </StyledCol>
            <StyledCol width={semiColWidth} offset={colOffset}>
              <TextField
                error={touched.fromEmail && Boolean(errors?.fromEmail)}
                fullWidth
                helperText={touched.fromEmail ? errors?.fromEmail : ''}
                id="fromEmail"
                label="Email"
                onChange={handleChange}
                type="email"
                value={values.fromEmail}
                disabled={isSubmitting}
                autoComplete="email"
                inputRef={emailRef}
              />
            </StyledCol>
            <StyledCol width={semiColWidth}>
              <ControlledSelect
                error={touched.state && Boolean(errors?.state)}
                helperText={touched.state ? errors?.state : ''}
                id="state"
                label="State"
                onChange={handleChange('state')}
                options={STATES.map((state) => ({ value: state, label: state }))}
                value={values.state}
              />
            </StyledCol>
            <StyledCol width={colWidth} offset={colOffset}>
              <TextField
                error={touched.message && Boolean(errors?.message)}
                fullWidth
                helperText={touched.message ? errors?.message : ''}
                id="message"
                label="Message"
                multiline
                onChange={handleChange}
                rows="4"
                value={values.message}
                disabled={isSubmitting}
                inputRef={messageRef}
              />
            </StyledCol>
            <StyledCol width={semiColWidth} offset={colOffset}>
              <Recaptcha
                error={touched.recaptcha && Boolean(errors?.recaptcha)}
                helperText={touched.recaptcha ? errors?.recaptcha : ''}
                id="recaptcha"
                sitekey={process.env.RECAPTCHA_SITE_KEY}
                onChange={handleChange('recaptcha')}
              />
            </StyledCol>
            <StyledCol width={semiColWidth}>
              <PrimaryButton
                eventText="Submit"
                type="submit"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                fullWidth
              >
                {isSubmitting ? 'Submitting…' : 'Submit'}
              </PrimaryButton>
            </StyledCol>
            {status && !isSuccess && (
              <ErrorCol id="general" width={colWidth} offset={colOffset}>
                <Alert status={ALERT_STATUSES.error} content={status} />
              </ErrorCol>
            )}
          </Row>
        </form>
      )}
    </>
  );
};

export default withFormik<ContactFormOuterProps & ContactFormFieldValues, ContactFormFieldValues>({
  mapPropsToValues: ({ firstName, lastName, fromEmail, state, message, recaptcha, subject }) => {
    return {
      action: 'contact-form/send',
      firstName,
      lastName,
      fromEmail,
      state,
      message,
      recaptcha,
      subject,
    };
  },

  validate: ({ recaptcha }) => {
    if (!recaptcha) {
      return { recaptcha: 'reCAPTCHA is required' };
    }
  },

  validationSchema: Yup.object().shape({
    firstName: formValidation.requiredFirstName,
    lastName: formValidation.requiredLastName,
    fromEmail: formValidation.requiredEmail,
    message: formValidation.requiredMessage,
  }),

  handleSubmit: async (values, bag) => {
    const { message, state, firstName, lastName, recaptcha, ...rest } = values;

    try {
      const response = await fetch('/api/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Recaptcha-Token': recaptcha || '',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email: rest.fromEmail,
          state,
          message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        logTrackingEvent({
          ...formEvent,
          form_step: 'submit',
        });

        bag.setStatus('success');
      } else {
        if (result.errors.general) {
          bag.setStatus(result.errors.general);
        }
        if (result.errors.fields) {
          bag.setErrors(result.errors.fields);
          // We have to mark fields as touched so server-side field errors display
          const touchedFields: Record<string, boolean> = {};
          Object.keys(result.errors.fields).forEach((field) => {
            touchedFields[field] = true;
          });
          bag.setTouched(touchedFields);
        }
      }
    } catch (error) {
      console.error('Form submission error: ', error);
      bag.setStatus('Something went wrong. Please try again.');
    }
  },
})(ContactForm);
